import { BrowserRouter as Router, Routes, Route, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useState, useEffect, createContext, useContext } from 'react'
import axios from 'axios'
import { getAuthorizationReturnUrl } from './oauthReturnUrl'

// Types
interface User {
  id: string
  email: string
  firstName: string
  lastName: string
  roles: string[]
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
}

interface RegisterData {
  email: string
  password: string
  confirmPassword: string
  firstName: string
  lastName: string
}

// Auth Context
const AuthContext = createContext<AuthContextType | null>(null)

function useAuth() {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}

function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      const response = await axios.get('/api/account/me')
      setUser(response.data)
    } catch {
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    const response = await axios.post('/api/account/login', { email, password })
    if (response.data.success) {
      setUser(response.data.user)
    } else {
      throw new Error(response.data.message)
    }
  }

  const register = async (data: RegisterData) => {
    const response = await axios.post('/api/account/register', data)
    if (response.data.success) {
      setUser(response.data.user)
    } else {
      throw new Error(response.data.errors?.join(', ') || response.data.message)
    }
  }

  const logout = async () => {
    await axios.post('/api/account/logout')
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gradient-to-br from-letech-dark via-letech-lilac-darker to-letech-dark">
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/profile" element={<Profile />} />
          </Routes>
        </div>
      </Router>
    </AuthProvider>
  )
}

function Home() {
  const { user, loading, logout } = useAuth()
  const navigate = useNavigate()

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-letech-lilac border-t-transparent"></div>
      </div>
    )
  }

  // Derive each app's URL from THIS portal's own origin, so a single build is correct in
  // every environment (the apps are sibling subdomains of "id"):
  //   local: id.local.letech.solutions:8443 -> <app>.local.letech.solutions:8443
  //   prod:  id.letech.solutions            -> <app>.letech.solutions
  const { protocol, hostname, port } = window.location
  const baseDomain = hostname.replace(/^id\./, '')
  const portSuffix = port ? `:${port}` : ''
  const appUrl = (sub: string) => `${protocol}//${sub}.${baseDomain}${portSuffix}`

  const apps = [
    { name: 'RecipeVault', color: 'from-orange-500 to-red-500', url: appUrl('recipevault'), icon: 'M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253' },
    { name: 'ColorCraft', color: 'from-indigo-500 to-purple-500', url: appUrl('colorcraft'), icon: 'M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01' },
    { name: 'TreasureLog', color: 'from-amber-500 to-yellow-500', url: appUrl('treasurelog'), icon: 'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4' },
    { name: 'CashFlow', color: 'from-green-500 to-emerald-500', url: appUrl('cashflow'), icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { name: 'GolfApp', color: 'from-green-600 to-teal-500', url: appUrl('golfapp'), icon: 'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z' },
    { name: 'GenderGenie', color: 'from-fuchsia-500 to-pink-500', url: appUrl('gendergenie'), icon: 'M5 3v4M3 5h4M6 17v4m-2-2h4m7-15l2.4 6.6L22 13l-6.6 2.4L13 22l-2.4-6.6L4 13l6.6-2.4L13 4z' },
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <img src="/logo-dot-let.svg" alt="LeTech" className="w-10 h-10" />
            <span className="text-xl font-bold text-white">LeTech ID</span>
          </div>
          <div className="flex items-center space-x-4">
            {user ? (
              <>
                <span className="text-gray-300">Welcome, {user.firstName}</span>
                <button
                  onClick={() => navigate('/profile')}
                  className="text-gray-300 hover:text-white transition-colors"
                >
                  Profile
                </button>
                <button
                  onClick={logout}
                  className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-gray-300 hover:text-white transition-colors">Sign In</Link>
                <Link to="/register" className="bg-letech-lime hover:bg-letech-lime-dark text-letech-dark font-semibold px-4 py-2 rounded-lg hover:shadow-lg hover:shadow-letech-lime/25 transition-all">
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold text-white mb-4">
            {user ? `Welcome back, ${user.firstName}!` : 'Central Authentication'}
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            {user
              ? 'Access all your apps with a single account. Click any app below to get started.'
              : 'One account for all your apps. Sign in once, access everywhere.'}
          </p>
        </div>

        {/* App Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {apps.map((app) => (
            <a
              key={app.name}
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-6 hover:bg-white/10 transition-all group"
            >
              <div className={`w-14 h-14 bg-gradient-to-br ${app.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={app.icon} />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-white mb-2">{app.name}</h3>
              <p className="text-gray-400 text-sm">Click to open →</p>
            </a>
          ))}
        </div>

        {!user && (
          <div className="mt-16 text-center">
            <div className="bg-gradient-to-r from-letech-lilac-dark/20 to-letech-lime/20 backdrop-blur border border-letech-lilac/20 rounded-2xl p-8 max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-4">Get Started Today</h2>
              <p className="text-gray-300 mb-6">
                Create your free LeTech ID and get access to all apps with single sign-on.
              </p>
              <Link
                to="/register"
                className="inline-block bg-letech-lime hover:bg-letech-lime-dark text-letech-dark px-8 py-3 rounded-xl font-semibold hover:shadow-xl hover:shadow-letech-lime/25 transition-all"
              >
                Create Account
              </Link>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}

function Login() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  // ASP.NET's cookie challenge sends the return path as "ReturnUrl" (capital R);
  // accept both casings so the OAuth flow continues after a form login.
  const rawReturnUrl = searchParams.get('ReturnUrl') || searchParams.get('returnUrl')
  const returnUrl = getAuthorizationReturnUrl(rawReturnUrl, window.location.origin)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      await login(email, password)
      // If there's a returnUrl with OAuth params, redirect there
      if (returnUrl) {
        window.location.href = returnUrl
      } else {
        navigate('/')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <img src="/logo-dot-let.svg" alt="LeTech" className="w-12 h-12" />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Welcome back</h1>
          <p className="text-gray-400">Sign in to your LeTech ID</p>
        </div>

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac focus:border-transparent"
                placeholder="••••••••"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-letech-lime hover:bg-letech-lime-dark text-letech-dark py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-letech-lime/25 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <p className="text-center text-gray-400 mt-6">
            Don't have an account?{' '}
            <Link to={returnUrl ? `/register?returnUrl=${encodeURIComponent(returnUrl)}` : '/register'} className="text-letech-lilac hover:text-letech-lilac-dark">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

function Register() {
  const { register } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const rawReturnUrl = searchParams.get('ReturnUrl') || searchParams.get('returnUrl')
  const returnUrl = getAuthorizationReturnUrl(rawReturnUrl, window.location.origin)
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  })
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return
    }

    setLoading(true)

    try {
      await register(formData)
      // Continue the OAuth flow if we arrived here mid-login.
      if (returnUrl) {
        window.location.href = returnUrl
      } else {
        navigate('/')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center space-x-2 mb-6">
            <img src="/logo-dot-let.svg" alt="LeTech" className="w-12 h-12" />
          </Link>
          <h1 className="text-3xl font-bold text-white mb-2">Create your LeTech ID</h1>
          <p className="text-gray-400">Get started with your free account</p>
        </div>

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">First Name</label>
                <input
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Last Name</label>
                <input
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac"
                placeholder="Min. 8 characters"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">Confirm Password</label>
              <input
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-letech-lilac"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-letech-lime hover:bg-letech-lime-dark text-letech-dark py-3 rounded-lg font-semibold hover:shadow-lg hover:shadow-letech-lime/25 transition-all disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </button>
          </form>

          <p className="text-center text-gray-400 mt-6">
            Already have an account?{' '}
            <Link to={returnUrl ? `/login?returnUrl=${encodeURIComponent(returnUrl)}` : '/login'} className="text-letech-lilac hover:text-letech-lilac-dark">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

function Profile() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  useEffect(() => {
    if (!user) navigate('/login')
  }, [user, navigate])

  if (!user) return null

  return (
    <div className="min-h-screen">
      <header className="bg-white/5 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center space-x-3">
            <img src="/logo-dot-let.svg" alt="LeTech" className="w-10 h-10" />
            <span className="text-xl font-bold text-white">LeTech ID</span>
          </Link>
          <button
            onClick={logout}
            className="bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            Sign Out
          </button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-16">
        <h1 className="text-3xl font-bold text-white mb-8">Your Profile</h1>

        <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-8">
          <div className="flex items-center space-x-6 mb-8">
            <div className="w-20 h-20 bg-gradient-to-br from-letech-lilac-dark to-letech-lime rounded-full flex items-center justify-center text-white text-2xl font-bold">
              {user.firstName.charAt(0)}{user.lastName.charAt(0)}
            </div>
            <div>
              <h2 className="text-2xl font-semibold text-white">{user.firstName} {user.lastName}</h2>
              <p className="text-gray-400">{user.email}</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex justify-between py-3 border-b border-white/10">
              <span className="text-gray-400">User ID</span>
              <span className="text-white font-mono text-sm">{user.id}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-white/10">
              <span className="text-gray-400">Email</span>
              <span className="text-white">{user.email}</span>
            </div>
            <div className="flex justify-between py-3 border-b border-white/10">
              <span className="text-gray-400">Name</span>
              <span className="text-white">{user.firstName} {user.lastName}</span>
            </div>
            <div className="flex justify-between py-3">
              <span className="text-gray-400">Roles</span>
              <span className="text-white">{user.roles.length > 0 ? user.roles.join(', ') : 'User'}</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}

export default App
